import { APICallError } from 'ai';
import { toast } from 'sonner';

import { api } from '@/lib/clients';

// Pull-related service layer - separates business logic from UI components
export class PullService {
  /**
   * Generate AI review for a pull request with enhanced error handling
   */
  static async generateReview(params: {
    owner: string;
    repo: string;
    pullNumber: number;
    installationId: number;
    reviewType?: 'full' | 'incremental' | 'range';
    fromCommit?: string;
    toCommit?: string;
    customPrompt?: string;
    onRetry?: () => void;
  }): Promise<{ success: boolean; error?: Error }> {
    try {
      const requestBody = {
        installationId: params.installationId,
        reviewType: params.reviewType,
        fromCommit: params.fromCommit,
        toCommit: params.toCommit,
        customPrompt: params.customPrompt,
      };
      
      const response = await api.github[':owner'][':repo']['pulls'][':pull_number']['review'].$post({
        json: requestBody,
        param: {
          owner: params.owner,
          repo: params.repo,
          pull_number: params.pullNumber.toString(),
        },
      });      
      const responseData = await response.json();
      console.log('[PullService] Response data:', responseData);

      const reviewTypeText =
        params.reviewType === 'incremental'
          ? 'Incremental review'
          : params.reviewType === 'range'
            ? 'Custom range review'
            : 'Full review';

      // Check if the review was skipped
      if ('skipped' in responseData && responseData.skipped) {
        toast.info(responseData.message || 'No new commits to review', {
          description: 'The last Mesa review already covers all commits in this PR.',
        });
      } else {
        toast.success(`${reviewTypeText} complete. View Mesa's review in the comments.`);
      }
      return { success: true };
    } catch (error) {
      console.error('Error generating review:', error);

      // Show user-friendly error messages
      this.showUserFriendlyError(error, 'review', params.onRetry);

      return { success: false, error: error instanceof Error ? error : new Error(String(error)) };
    }
  }

  /**
   * Generate AI walkthrough for a pull request with enhanced error handling
   */
  static async generateWalkthrough(params: {
    owner: string;
    repo: string;
    pullNumber: number;
    onRetry?: () => void;
  }): Promise<{ success: boolean; error?: Error }> {
    try {
      await api.github[':owner'][':repo']['pulls'][':pull_number']['walkthrough'].$post({
        param: {
          owner: params.owner,
          repo: params.repo,
          pull_number: params.pullNumber.toString(),
        },
      });

      toast.success('Walkthrough generation started successfully');
      return { success: true };
    } catch (error) {
      console.error('Error generating walkthrough:', error);

      // Show user-friendly error messages
      this.showUserFriendlyError(error, 'walkthrough', params.onRetry);

      return { success: false, error: error instanceof Error ? error : new Error(String(error)) };
    }
  }


  /**
   * Show user-friendly error messages based on error type
   */
  private static showUserFriendlyError(error: any, operation: 'review' | 'walkthrough', onRetry?: () => void) {
    const operationName = operation === 'review' ? 'Review' : 'Walkthrough';

    // Use AI SDK's built-in error classification
    if (APICallError.isInstance(error)) {
      switch (error.statusCode) {
        case 429:
          toast.error(`${operationName} temporarily unavailable`, {
            description: "We're experiencing high demand. Please try again in a few minutes.",
            action: onRetry
              ? {
                  label: 'Retry',
                  onClick: onRetry,
                }
              : undefined,
          });
          break;

        case 402:
          toast.error(`${operationName} service unavailable`, {
            description: 'AI service quota exceeded. Our team has been notified.',
            action: {
              label: 'Contact Support',
              onClick: () => window.open('mailto:support@mesa.dev?subject=AI Service Issue', '_blank'),
            },
          });
          break;

        case 413:
          toast.error(`Pull request too large for ${operation}`, {
            description: 'This PR has too many changes to process. Try reviewing individual files.',
            action: onRetry
              ? {
                  label: 'Retry',
                  onClick: onRetry,
                }
              : undefined,
          });
          break;

        case 500:
        case 502:
        case 503:
        case 504:
          toast.error(`${operationName} connection error`, {
            description: 'Server issue detected. Please try again in a moment.',
            action: onRetry
              ? {
                  label: 'Retry',
                  onClick: onRetry,
                }
              : undefined,
          });
          break;

        default:
          toast.error(`${operationName} failed`, {
            description: 'An unexpected error occurred. Our team has been notified.',
            action: onRetry
              ? {
                  label: 'Retry',
                  onClick: onRetry,
                }
              : undefined,
          });
          break;
      }
    } else {
      // Handle non-API errors
      const message = error?.message?.toLowerCase() || '';

      if (message.includes('network') || message.includes('timeout') || message.includes('connection')) {
        toast.error(`${operationName} connection error`, {
          description: 'Network issue detected. Please check your connection and try again.',
          action: onRetry
            ? {
                label: 'Retry',
                onClick: onRetry,
              }
            : undefined,
        });
      } else {
        toast.error(`${operationName} failed`, {
          description: 'An unexpected error occurred. Please try again.',
          action: onRetry
            ? {
                label: 'Retry',
                onClick: onRetry,
              }
            : undefined,
        });
      }
    }
  }
}
